import { useState, useEffect, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { MapPin, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onSelect?: (place: AddressSearchResult) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export interface AddressSearchResult {
  displayName: string
  address: string
  lat: number
  lon: number
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Search for your address...',
  className,
  disabled = false
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const debounceTimeout = useRef<NodeJS.Timeout | undefined>(undefined)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Format address to clean format: "Street Number, Postal City, Country"
  const formatAddress = (addressDetails: any): string => {
    const road = addressDetails.road || addressDetails.pedestrian || ''
    const houseNumber = addressDetails.house_number || ''
    const postcode = addressDetails.postcode || ''
    
    const city = addressDetails.city || 
                 addressDetails.town || 
                 addressDetails.village || 
                 addressDetails.municipality ||
                 addressDetails.county ||
                 ''
    
    const country = addressDetails.country || ''
    
    const parts: string[] = []
    
    // Street and number: "Street Number"
    if (road && houseNumber) {
      parts.push(`${road} ${houseNumber}`)
    } else if (road) {
      parts.push(road)
    }
    
    // Postal code and city: "Postal City"
    if (postcode && city) {
      parts.push(`${postcode} ${city}`)
    } else if (city) {
      parts.push(city)
    } else if (postcode) {
      parts.push(postcode)
    }
    
    // Country
    if (country) {
      parts.push(country)
    }
    
    return parts.join(', ')
  }

  // Search for addresses using Nominatim
  const searchAddresses = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setSuggestions([])
      return
    }

    setIsLoading(true)
    try {
      const encodedQuery = encodeURIComponent(searchQuery.trim())
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=5&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'GetMoreTats-Admin/1.0'
          }
        }
      )

      if (!response.ok) {
        throw new Error(`Search API error: ${response.status}`)
      }

      const data = await response.json()

      const results: AddressSearchResult[] = data.map((item: any) => {
        const addressDetails = item.address || {}
        const formattedAddress = formatAddress(addressDetails)

        return {
          displayName: item.display_name,
          address: formattedAddress,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon)
        }
      })

      setSuggestions(results)
      setShowSuggestions(true)
    } catch (error) {
      console.error('Address search error:', error)
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }

  // Debounce search queries
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    debounceTimeout.current = setTimeout(() => {
      searchAddresses(value)
    }, 300)

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [value])

  const handleInputChange = (newValue: string) => {
    onChange(newValue)
    setSelectedIndex(-1)
  }

  const handleSelectSuggestion = (suggestion: AddressSearchResult) => {
    onChange(suggestion.address)
    setShowSuggestions(false)
    setSuggestions([])
    if (onSelect) {
      onSelect(suggestion)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setSelectedIndex(prevIndex => 
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        setSelectedIndex(prevIndex => 
          prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
        )
        break
      case 'Enter':
        event.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex])
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        break
    }
  }

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true)
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className={cn('pr-10', className)}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : (
            <MapPin className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={cn(
                'w-full px-4 py-3 text-left hover:bg-accent transition-colors flex items-start gap-2',
                selectedIndex === index && 'bg-accent'
              )}
            >
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {suggestion.address}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2">
                  {suggestion.displayName}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

